import * as React from 'react';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import { withRouter, RouteComponentProps } from 'react-router';
import { compose } from 'redux';
import { Drawer } from 'components/';
import * as Queries from 'api/graphql/queries';
import { adminProductList, AdminProductFilters, AdminProductOrderField, OrderDirection } from '@api/graphql/types';
import GraphQLClient from '@api/graphql/GraphQLClient';
import ControlledTable from '@components/ControlledTable';
import { RowInfo } from 'react-table';
import { Column } from 'react-virtualized';
import { Paths } from '..';
import { BooleanCell, OpenInNewCell } from '@components/Table/Cell';
import ProductListFilters from '../components/ProductListFilters';
import { isNull } from 'util';
import { DispatchProp, connect } from 'react-redux';
import { ApplicationState } from 'reducers/';
import { updateListOptions } from '@actions/listActions';
import { ListOptionType } from '@reducers/listReducer';

interface StateType {
    data: any[];
    paginatorInfo: any;
}

export const ProductListDefaultOptions = {
    sortBy: {
        field: AdminProductOrderField.ID,
        direction: OrderDirection.DESC,
    },
    count: 10,
    page: 0,
    filters: {
        name: '',
        ext_id: '',
        category: [],
        is_special: null,
        is_new: null,
        productStatus: null,
        is_mpl: null,
    },
};

const listName = 'productList';

interface ReduxProps {
    listOptions: ListOptionType;
}

type Props = ReduxProps & InjectedIntlProps & RouteComponentProps & DispatchProp;

class ProductList extends React.Component<Props, StateType> {

    public state = {
        data: [],
        paginatorInfo: {},
    };

    private StatusCell = (row: any) => {
        return this.props.intl.formatMessage({ id: `component.ProductStatusSelect.option.${row.value}` });
    }

    private NameCell = (cellInfo: any) => {
        const columnName = "name";
        return (
            <span className="nameCell" data-tip={cellInfo.original[columnName]}>
                {cellInfo.original[columnName]}
            </span>
        );
    }

    private OpenCell = OpenInNewCell((rowInfo: any) => {
        return `${Paths.product}/${rowInfo.original.ext_id}`;
    });

    private tableColumns = [
        {Header: '', sortable: false, Cell: this.OpenCell, width: 30},
        this.getDefaultColumnByName('ext_id', { sortable: true, width: 60 }),
        this.getDefaultColumnByName('name', { Cell: this.NameCell, sortable: true, width: 500 }),
        this.getDefaultColumnByName('status', { sortable: true, Cell: this.StatusCell }),
        this.getDefaultColumnByName('price', { sortable: true, width: 60 }),
        this.getDefaultColumnByName('special_price', { width: 60 }),
        this.getDefaultColumnByName('special_to_date'),
        this.getDefaultColumnByName('news_from_date'),
        this.getDefaultColumnByName('news_to_date'),
        this.getDefaultColumnByName('is_mpl', { Cell: BooleanCell, width: 50 }),
    ];

    public componentDidMount() {
        this.fetchData();
    }

    public componentDidUpdate(prevProps: Props) {
        if (prevProps.listOptions !== this.props.listOptions) {
            this.fetchData();
        }
    }

    private getDefaultColumnByName(name: string, options: any = {}) {
        return {
            Header: this.label(`${name}.title`),
            accessor: name,
            sortable: false,
            ...options,
        };
    }

    private label(id: string): string {
        return this.props.intl.formatMessage({ id: `module.product.${id}` });
    }

    private getFilters() {
        const result = { ...this.props.listOptions.filters };
        Object.keys(result).forEach((key) => isNull(result[key]) && delete result[key]);
        if (result.category && !result.category.length) {
            result.category = undefined;
        }
        if (result.name === '') {
            result.name = undefined;
        }
        if (result.ext_id === '') {
            result.ext_id = undefined;
        }
        return result;
    }

    private getSortBy() {
        return {
            field: this.props.listOptions.sortBy.field.toUpperCase(),
            direction: this.props.listOptions.sortBy.direction.toUpperCase(),
        };
    }

    private fetchData() {
        const variables = { ...this.props.listOptions, page: this.props.listOptions.page + 1, sortBy: this.getSortBy(), filters: this.getFilters() };
        GraphQLClient.query<adminProductList>({
            query: Queries.adminProductList,
            variables,
        }).then(response => {
            const data = response.data.adminProducts ? response.data.adminProducts.data : [];
            const paginatorInfo = response.data.adminProducts ? response.data.adminProducts.paginatorInfo : {};
            this.setState({ data, paginatorInfo });
        });
    }

    private onOptionsChange = (options: any) => {
        this.props.dispatch(updateListOptions(listName, options));
    }

    private setFilters = (values: AdminProductFilters) => {
        const options = {
            ...this.props.listOptions,
            filters: values,
            page: 0,
        };
        this.onOptionsChange(options);
    }


    private onRowClick = (finalState: any, rowInfo?: RowInfo, column?: Column, instance?: any): object | undefined => {
        if (rowInfo) {
            return {
                onClick: () => {
                    this.props.history.push(`${Paths.product}/${rowInfo.original.ext_id}`);
                },
                style: {
                    cursor: 'pointer',
                },
            };
        }
        return {};
    }


    public render() {
        return (
            <Drawer>
                <div className="row">
                    <div className="col-lg-12">
                        <div className="ibox float-e-margins">
                            <div className="ibox-content">
                                <ProductListFilters value={this.props.listOptions.filters} onSubmit={this.setFilters} />
                                <ControlledTable
                                    listName={listName}
                                    defaultOptions={ProductListDefaultOptions}
                                    columns={this.tableColumns}
                                    options={this.props.listOptions}
                                    data={this.state.data}
                                    paginatorInfo={this.state.paginatorInfo}
                                    onRowClick={this.onRowClick}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Drawer>
        );
    }
}

const mapStateToProps = ({ lists }: ApplicationState): ReduxProps => {
    return { listOptions: (lists ? (lists[listName] || ProductListDefaultOptions) : ProductListDefaultOptions) };
};

export default compose(
    withRouter,
    injectIntl,
    connect(mapStateToProps),
)(ProductList);



// WEBPACK FOOTER //
// ./src/modules/Product/pages/ProductList.tsx